import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTimes, FaTrophy, FaRedo, FaUserTie, FaShare, FaTwitter, FaLinkedin } from 'react-icons/fa';
import { getCurrentSchool } from '../config/schools';
import api from '../utils/api';
import ReportErrorButton from '../components/ReportErrorButton';

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Composant Badge partageable
function ShareableBadge({ score, total, category, onClose }) {
  const percentage = Math.round((score / total) * 100);
  
  const shareOnTwitter = () => {
    const text = `J'ai obtenu ${score}/${total} au quiz "${category}" sur @DailyGlobeSchool ! #Journalisme #QuiEstQui`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        className="bg-gradient-to-br from-purple-500 via-blue-500 to-teal-500 p-1 rounded-2xl max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl p-6 text-center">
          <FaTrophy className="text-6xl mx-auto mb-4 text-yellow-500" />
          <h2 className="comic-title text-2xl text-gray-800 mb-2">Quiz Terminé !</h2>
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-lg mb-4">
            <p className="text-lg font-bold">{category}</p>
          </div>
          <div className="text-4xl font-bold text-green-500 mb-2">{score}/{total}</div>
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <span key={i} className={`text-2xl ${i < Math.ceil(score * 5 / total) ? '' : 'grayscale opacity-30'}`}>⭐</span>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-4">DailyGlobe School - {new Date().toLocaleDateString('fr-FR')}</p>
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-3">Partager mon score :</p>
            <div className="flex justify-center gap-3">
              <button onClick={shareOnTwitter} className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-lg"><FaTwitter /> Twitter</button>
              <button onClick={shareOnLinkedIn} className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg"><FaLinkedin /> LinkedIn</button>
            </div>
          </div>
          <button onClick={onClose} className="mt-4 text-gray-500 hover:text-gray-700 text-sm">Fermer</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const CATEGORIES = [
  { id: 'french_gov', name: 'Gouvernement Français', color: '#1B4965' },
  { id: 'world_leaders', name: 'Dirigeants Mondiaux', color: '#E63946' },
  { id: 'ceo_nasdaq', name: 'Patrons NASDAQ', color: '#FFB703' },
  { id: 'ceo_cac40', name: 'Patrons CAC 40', color: '#62B6CB' },
  { id: 'french_politics', name: 'Politique Française', color: '#9B59B6' },
  { id: 'unions', name: 'Syndicats', color: '#E74C3C' },
  { id: 'media_bosses', name: 'Patrons de Presse', color: '#2ECC71' },
  { id: 'top_journalists', name: 'Top 100 Journalistes', color: '#3498DB' }
];

function WhoIsWhoPage() {
  const navigate = useNavigate();
  const school = getCurrentSchool();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [reportedQuestions, setReportedQuestions] = useState(new Set());

  const startQuiz = async (category) => {
    setLoading(true);
    setSelectedCategory(category);
    try {
      const response = await api.get(`/who-is-who/questions?category=${category.id}`);
      setQuestions(response.data);
      
      // Mélanger les options de chaque question
      const shuffled = response.data.map(q => {
        const optionsWithIndex = q.options.map((opt, idx) => ({ text: opt, originalIndex: idx }));
        const shuffledOptions = shuffleArray(optionsWithIndex);
        const newCorrectIndex = shuffledOptions.findIndex(opt => opt.originalIndex === q.correctIndex);
        
        return {
          ...q,
          options: shuffledOptions.map(opt => opt.text),
          correctIndex: newCorrectIndex,
          originalCorrectIndex: q.correctIndex
        };
      });
      
      setShuffledQuestions(shuffled);
      setCurrentIndex(0);
      setScore(0);
      setGameOver(false);
      setShowResult(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      alert('Erreur lors du chargement des questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answerIndex) => {
    if (selectedAnswer !== null || !shuffledQuestions[currentIndex]) return;
    
    const currentQuestion = shuffledQuestions[currentIndex];
    const isCorrect = answerIndex === currentQuestion.correctIndex;
    
    setSelectedAnswer(answerIndex);
    setFeedback({
      isCorrect,
      correctAnswer: currentQuestion.options[currentQuestion.correctIndex],
      explanation: currentQuestion.explanation
    });
    
    if (isCorrect) {
      setScore(s => s + 1);
    }
    
    setShowResult(true);
  };

  const nextQuestion = () => {
    if (currentIndex < shuffledQuestions.length - 1) {
      setCurrentIndex(i => i + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setFeedback(null);
    } else {
      setGameOver(true);
    }
  };

  const resetQuiz = () => {
    setSelectedCategory(null);
    setQuestions([]);
    setShuffledQuestions([]);
    setCurrentIndex(0);
    setScore(0);
    setGameOver(false);
    setShowResult(false);
    setSelectedAnswer(null);
    setFeedback(null);
    setShowBadge(false);
  };

  const currentQuestion = shuffledQuestions[currentIndex];

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Header */}
      <header className="border-b-4 border-black" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="comic-button bg-white text-black"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline mr-2" /> Retour
          </button>
          <div className="flex items-center space-x-4">
            <FaUserTie className="text-3xl text-white" />
            <h1 className="text-2xl md:text-3xl comic-title text-white">Qui est Qui ?</h1>
          </div>
          <div className="text-white text-right">
            {selectedCategory && !gameOver && shuffledQuestions.length > 0 && (
              <p className="comic-text text-lg">Score: {score}/{shuffledQuestions.length}</p>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Category Selection */}
        {!selectedCategory && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <div className="text-center mb-8">
              <h2 className="comic-title text-4xl mb-4" style={{ color: '#1B4965' }}>
                Choisissez une catégorie
              </h2>
              <p className="text-lg text-gray-600">
                Testez vos connaissances sur les personnalités importantes
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {CATEGORIES.map((category, idx) => (
                <motion.button
                  key={category.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => startQuiz(category)}
                  className="comic-panel p-6 text-center cursor-pointer hover:shadow-xl transition-shadow"
                  style={{ borderColor: category.color }}
                  data-testid={`category-${category.id}`}
                >
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ background: category.color }}
                  >
                    <FaUserTie className="text-2xl text-white" />
                  </div>
                  <h3 className="comic-title text-xl" style={{ color: category.color }}>
                    {category.name}
                  </h3>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-20">
            <div className="onomatopoeia text-4xl">LOADING...</div>
          </div>
        )}

        {/* Quiz */}
        {selectedCategory && !loading && !gameOver && currentQuestion && (
          <motion.div
            key={currentIndex}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
          >
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="comic-text">Question {currentIndex + 1}/{shuffledQuestions.length}</span>
                <span 
                  className="comic-badge text-white px-3 py-1"
                  style={{ background: selectedCategory.color }}
                >
                  {selectedCategory.name}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 border-2 border-black">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${((currentIndex + 1) / shuffledQuestions.length) * 100}%`,
                    background: selectedCategory.color
                  }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="comic-panel mb-6">
              <h3 className="comic-title text-2xl mb-2" style={{ color: '#1B4965' }}>
                {currentQuestion.question}
              </h3>
              {currentQuestion.context && (
                <p className="text-gray-600 italic">{currentQuestion.context}</p>
              )}
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {currentQuestion.options.map((option, idx) => {
                let buttonStyle = 'bg-white hover:bg-yellow-50';
                if (showResult) {
                  if (idx === currentQuestion.correctIndex) {
                    buttonStyle = 'bg-green-100 border-green-500';
                  } else if (idx === selectedAnswer && idx !== currentQuestion.correctIndex) {
                    buttonStyle = 'bg-red-100 border-red-500';
                  }
                }
                
                return (
                  <motion.button
                    key={idx}
                    whileHover={!showResult ? { scale: 1.02 } : {}}
                    whileTap={!showResult ? { scale: 0.98 } : {}}
                    onClick={() => handleAnswer(idx)}
                    disabled={showResult}
                    className={`comic-panel p-4 text-left transition-all ${buttonStyle}`}
                    data-testid={`option-${idx}`}
                  >
                    <div className="flex items-center space-x-3">
                      <span 
                        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
                        style={{ background: selectedCategory.color }}
                      >
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="comic-text text-lg">{option}</span>
                      {showResult && idx === currentQuestion.correctIndex && (
                        <FaCheck className="text-green-600 ml-auto" />
                      )}
                      {showResult && idx === selectedAnswer && idx !== currentQuestion.correctIndex && (
                        <FaTimes className="text-red-600 ml-auto" />
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Bouton signaler une erreur */}
            <div className="flex justify-end mb-4">
              <ReportErrorButton
                quizId={`who-is-who-${selectedCategory?.id}`}
                questionId={`q-${currentIndex}`}
                questionText={currentQuestion.question}
                options={currentQuestion.options}
                onReported={(qId) => {
                  setReportedQuestions(prev => new Set([...prev, currentIndex]));
                  // Si la question est signalée, on passe à la suivante automatiquement
                  setTimeout(() => nextQuestion(), 1500);
                }}
              />
            </div>

            {/* Feedback */}
            <AnimatePresence>
              {feedback && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  className={`comic-panel mb-6 ${feedback.isCorrect ? 'bg-green-50' : 'bg-red-50'}`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    {feedback.isCorrect ? (
                      <>
                        <div className="onomatopoeia text-green-600 text-xl">BRAVO!</div>
                        <span className="text-green-600 font-bold">Bonne réponse!</span>
                      </>
                    ) : (
                      <>
                        <div className="onomatopoeia text-red-600 text-xl">RATÉ!</div>
                        <span className="text-red-600 font-bold">
                          La bonne réponse était: {feedback.correctAnswer}
                        </span>
                      </>
                    )}
                  </div>
                  {feedback.explanation && (
                    <p className="text-gray-700">{feedback.explanation}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Next Button */}
            {showResult && (
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                onClick={nextQuestion}
                className="comic-button comic-button-navy w-full text-xl py-4"
                data-testid="next-question"
              >
                {currentIndex < shuffledQuestions.length - 1 ? 'Question Suivante' : 'Voir les Résultats'}
              </motion.button>
            )}
          </motion.div>
        )}

        {/* Badge modal */}
        <AnimatePresence>
          {showBadge && (
            <ShareableBadge 
              score={score} 
              total={shuffledQuestions.length} 
              category={selectedCategory?.name}
              onClose={() => setShowBadge(false)} 
            />
          )}
        </AnimatePresence>

        {/* Game Over */}
        {gameOver && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="comic-panel max-w-2xl mx-auto text-center"
          >
            <FaTrophy className="text-8xl mx-auto mb-6 text-yellow-500" />
            <h2 className="comic-title text-4xl mb-4" style={{ color: '#1B4965' }}>
              Quiz Terminé!
            </h2>
            <div className="mb-6">
              <p className="text-6xl font-bold" style={{ color: selectedCategory.color }}>
                {score}/{shuffledQuestions.length}
              </p>
              <p className="text-xl text-gray-600">
                {score === shuffledQuestions.length ? 'Parfait! Tu connais tout le monde!' :
                 score >= shuffledQuestions.length * 0.7 ? 'Excellent! Tu as de bonnes connaissances!' :
                 score >= shuffledQuestions.length * 0.5 ? 'Pas mal! Continue à t\'informer!' :
                 'Il y a du travail! Lis plus d\'actualités!'}
              </p>
            </div>
            
            <div className="speech-bubble mb-6">
              <p className="comic-text">
                Un bon journaliste doit connaître les acteurs de l'actualité. 
                Continue à t'entraîner!
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowBadge(true)}
                className="comic-button bg-yellow-400 text-black"
              >
                <FaShare className="inline mr-2" /> Partager
              </button>
              <button
                onClick={() => startQuiz(selectedCategory)}
                className="comic-button comic-button-navy"
                data-testid="retry-button"
              >
                <FaRedo className="inline mr-2" /> Rejouer cette catégorie
              </button>
              <button
                onClick={resetQuiz}
                className="comic-button"
                data-testid="change-category"
              >
                Changer de catégorie
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default WhoIsWhoPage;
